import { Component, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  DsBoxModule,
  DsRowData,
  DsIconModule,
  DsButtonModule,
  DsLinkModule,
  DsSkeletonModule,
  DsFormsModule,
} from '@bmw-ds/components';
import { ICDQuartzTheme } from '../../shared/ag-theme';
import { ApiService } from 'src/app/services/api.service';
import { Router } from '@angular/router';
import {
  DsLoadingAreaConfiguration,
  DsLoadingAreaService,
} from '@bmw-ds/components';
import { CampaignInventoryInternalSearch } from '../campaign_inventory_internal_search/campaign-inventory-internal-search/campaign-inventory-internal-search';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import {
  GridApi,
  GridReadyEvent,
  ColDef,
  GridOptions,
  ModuleRegistry,
  AllCommunityModule,
  Theme,
} from 'ag-grid-community';
import { ViewDataRendererComponent } from '../../shared/view-data-renderer/view-data-renderer';
import { CommonService } from 'src/app/services/common.service';
import { ExportService } from 'src/app/services/export.service';
ModuleRegistry.registerModules([AllCommunityModule]);
@Component({
  selector: 'app-campaign-inventory-internal-region',
  imports: [
    CommonModule,
    DsBoxModule,
    CampaignInventoryInternalSearch,
    AgGridModule,
    AgGridAngular,
    DsIconModule,
    DsButtonModule,
    DsLinkModule,
    DsSkeletonModule,
    DsFormsModule,
  ],
  templateUrl: './campaign-inventory-internal-region.html',
  styleUrls: ['./campaign-inventory-internal-region.scss'],
  standalone: true,
})
export class CampaignInventoryInternalRegion implements OnInit {
  theme: Theme | 'legacy' = ICDQuartzTheme;
  noRowsTemplate =
    '<div class="icd-no-rows" role="status" aria-live="polite">No Rows Found</div>';
  paginationPageSize: number = 10;
  paginationPageSizeSelector: number[] | boolean = [10, 20, 30];
  rowData: DsRowData[] = [];
  private gridApi!: GridApi;
  dynamicTableHeight: number = 540;
  private readonly ROW_HEIGHT = 40;
  private readonly HEADER_HEIGHT = 50;
  private readonly FLOATING_FILTER_HEIGHT = 30;
  private readonly PAGINATION_PANEL_HEIGHT = 48;
  private readonly MAX_VISIBLE_ROWS = 20;
  regionTableData: any;
  selectedValue: any;
  loadingAreaConfig: DsLoadingAreaConfiguration = {
    id: 'bodyLoading',
    loadingTitle: 'Details are being displayed, this may take few seconds..',
    loadingText:
      'Please do not close the browser window. wait while we load data.',
  };
  nationalScreenSelectedValue: any;
  userEnteredDefectCode: string = '';
  regionFilterValue: any;
  isRegionFilterActive: boolean = false;
  constructor(
    private route: Router,
    private apiService: ApiService,
    private router: Router,
    private loadingAreaService: DsLoadingAreaService,
    private commonService: CommonService,
    private exportService: ExportService
  ) {}

  ngOnInit() {
    this.rowData = [];
    //  this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
    //  console.log('Regional Screen Selected Value:', this.commonService.nationalScreenSelectedValue.getValue());
    this.router.routerState.root.queryParams.subscribe((params: any) => {
      this.nationalScreenSelectedValue = params?.['value'] || this.commonService.nationalScreenSelectedValue.getValue() || '';
      // console.log('Selected Value from query params or service:', this.nationalScreenSelectedValue);
    });
    this.commonService.defectCode.subscribe((value) => {
      this.userEnteredDefectCode = value;
      this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
      this.commonService.defectCodeTableData.subscribe((data) => {
        this.regionTableData = data;
        this.rowData = this.regionTableData.map((row: any) => {
          return {
            Region: row['marketCodeAndName'] ?? null,
            VehiclesForWhichAllCampaignsCanBeCompleted:
              row['allCampaignCompleted'] ?? null,
            VehiclesForWhichSomeCampaignsCanBeCompleted:
              row['someCampaignCompleted'] ?? null,
            AllVehiclesInInventory: row['allVehicles'] ?? null,
          };
        });
        this.calculateDynamicHeight();
        // this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id); // Removes the loading area
      });
    });
  }

  onDataViewClick(params: any) {
    // console.log('View Data Clicked', params);
    this.selectedValue = params.value ?? '';
    // console.log('Selected Value from region:', this.selectedValue);
    this.commonService.regionalScreenSelectedValue.next(this.selectedValue);
    const columnName =
      params?.colDef?.field ?? params?.column?.getColId?.() ?? '';
    this.router.navigate(['campaign_inventory_internal_market'], {
      queryParams: {
        column: columnName,
        value: this.selectedValue,
        defectCode: params.data?.DefectCode ?? null,
      },
    });
  }
  onRegionFilterChanged() {
    this.trackRegionFilter(); // ← update isRegionFilterActive FIRST
    this.calculateDynamicHeight(); // ← then use updated flag for message
  }
  trackRegionFilter() {
    if (!this.gridApi) return;
    const activeFilters = this.gridApi.getFilterModel();
    this.regionFilterValue = activeFilters['Region']?.filter ?? '';
    this.isRegionFilterActive = !!this.regionFilterValue;
  }
  private getFilteredRowData(): any[] {
    if (!this.gridApi) return this.rowData;

    const filteredRows: any[] = [];

    // ← iterates only visible rows after filter + sort applied
    this.gridApi.forEachNodeAfterFilterAndSort((node: any) => {
      if (node.data) {
        filteredRows.push(node.data);
      }
    });

    // console.log('Filtered rows for PDF:', filteredRows.length, 'of', this.rowData.length);
    return filteredRows.length > 0 ? filteredRows : this.rowData;
  }

  windowPrint() {
    const columns = [
      'Region',
      'VehiclesForWhichAllCampaignsCanBeCompleted',
      'VehiclesForWhichSomeCampaignsCanBeCompleted',
      'AllVehiclesInInventory',
    ];
    const columnHeaders = [
      'Market-Market Code',
      'Vehicles for which all Campaign(s) can be completed',
      'Vehicles for which some Campaign(s) can be completed',
      'All vehicles in Inventory',
    ];
    const currentDate = new Date().toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const currentTime = new Date().toLocaleTimeString('en-US', {
      timeZone: 'America/New_York',
    });

    const details: Array<{ label: string; value: any }> = [
      { label: 'Defect Code', value: this.userEnteredDefectCode || '' },
      { label: 'Details as of', value: currentDate + ' at ' + currentTime },
      ...(this.isRegionFilterActive
        ? [
            {
              label: 'Market-Market code contains:',
              value: this.regionFilterValue || '',
            },
          ]
        : []),
    ];

    const printedOn = this.exportService.formatEstTimestamp();

    this.exportService.exportPdfWithDetails(
      'Inventory_Campaign_Details_Regional',
      this.getFilteredRowData(),
      columns,
      columnHeaders,
      details,
      {
        title: 'ROSS - Inventory Campaign',
        subtitle:
          'Inventory Campaign Details (Internal)-Regional - ' +
          this.nationalScreenSelectedValue,
        printedOn,
        orientation: 'landscape',
        rowHeight: 28,
        cellPadding: 5,
        columnWidths: [60, 120, 120, 80],
        zebraFillColor: [245, 245, 245],
        headerFillColor: [227, 242, 253],
      }
    );
  }

  goToNationalPage() {
    this.router.navigate(['campaign_inventory_internal_national']);
  }

  columns: ColDef[] = [
    {
      field: 'Region',
      headerName: 'Market-Market Code',
      floatingFilter: true,
      filter: true,
      flex: 1,
      cellRenderer: ViewDataRendererComponent,
      cellRendererParams: {
        onClick: this.onDataViewClick.bind(this),
      },
    },
    {
      field: 'VehiclesForWhichAllCampaignsCanBeCompleted',
      headerName: 'Vehicles For Which All Campaigns Can Be Completed',
      sortable: true,
      flex: 1,
    },
    {
      field: 'VehiclesForWhichSomeCampaignsCanBeCompleted',
      headerName: 'Vehicles For Which Some Campaigns Can Be Completed',
      sortable: true,
      flex: 1,
    },
    {
      field: 'AllVehiclesInInventory',
      headerName: 'All Vehicles In Inventory',
      sortable: true,
      flex: 1,
    },
  ];

  defaultColDef: ColDef = {
    flex: 1,
    minWidth: 100,
    resizable: true,
    floatingFilter: true,
    wrapHeaderText: true,
    autoHeight: true,
    headerCheckboxSelectionFilteredOnly: true,
    filterParams: {
      closeOnApply: true,
      suppressAndOrCondition: true,
      maxNumConditions: 1,
      trimInput: false,
      filterOptions: [
        // ? explicitly list all filter options you want
        'contains',
      ],
      defaultOption: 'contains',
    },
  };

  gridOptions: GridOptions = {
    suppressRowClickSelection: true,
    rowSelection: 'multiple',
    animateRows: true,
    enableCellTextSelection: true,
    ensureDomOrder: true,
    floatingFiltersHeight: 30,
    suppressHorizontalScroll: true,
  };

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    // console.log('Grid API:', this.gridApi);
    this.fitGridColumns();
    this.calculateDynamicHeight();
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.fitGridColumns();
  }

  private fitGridColumns() {
    if (!this.gridApi) return;
    setTimeout(() => {
      if (this.gridApi) {
        this.gridApi.sizeColumnsToFit();
      }
    }, 0);
  }

  onPaginationChanged() {
    this.calculateDynamicHeight();
  }

  onFilterChanged() {
    this.calculateDynamicHeight();
  }

  private calculateDynamicHeight() {
    if (!this.gridApi) return;

    const displayedRowCount = this.gridApi.getDisplayedRowCount();
    const currentPage = this.gridApi.paginationGetCurrentPage();
    const pageSize = this.gridApi.paginationGetPageSize();
    const startIndex = currentPage * pageSize;
    const visibleRowsOnCurrentPage = Math.max(
      Math.min(displayedRowCount - startIndex, pageSize),
      0
    );
    const cappedRowCount = Math.min(
      Math.max(visibleRowsOnCurrentPage, 1),
      this.MAX_VISIBLE_ROWS
    );

    const chromeHeight =
      this.HEADER_HEIGHT +
      this.FLOATING_FILTER_HEIGHT +
      this.PAGINATION_PANEL_HEIGHT;

    this.dynamicTableHeight = chromeHeight + cappedRowCount * this.ROW_HEIGHT;
    if (displayedRowCount === 0) {
      if (this.isRegionFilterActive) {
        this.noRowsTemplate = `
          <div class="icd-no-rows" role="status" aria-live="polite">
            <span>No matching rows found for the applied filter.</span>
            <br/>
            <span style="font-size:12px; color:#888;">Try adjusting or clearing the column filter.</span>
          </div>`;
      } else {
        this.noRowsTemplate = `<div class="icd-no-rows" role="status" aria-live="polite">No Rows Found</div>`;
      }
      this.gridApi.showNoRowsOverlay?.();
    } else {
      this.gridApi.hideOverlay?.();
    }
  }

  onRowClicked(event: any) {
    // console.log('Row clicked: ', event.data);
  }

  firstColumnNavigation() {
    if (this.gridApi) {
      const selectedRows = this.gridApi.getSelectedRows();
      // console.log('Selected option :', selectedRows);
      const selectedFirstColumn = selectedRows.map((row) => {
        return row?.[Object.keys(row)[0]];
      });
      // console.log('Confirm Not in Inventory Rows:', selectedFirstColumn);
    }
  }
}
